import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import Text from 'components/elements/texts/Text'
import DOMAIN from 'constants/domains'
import React from 'react'
import styled from 'styled-components'
import { getDomain, isKsDomain, isSaficDomain } from 'utils/domain'
import { PopupWindow } from './PopupWindow'
import { PAGE_PATH } from 'constants/pagePath'
import { useLocation } from 'react-router-dom'
import { SCREEN_STATE } from 'components/pages/Informed'
import InformedAnimation from './InformedAnimation'

const isMessageProps = (target: any): target is MessageProps => {
  return target.message !== undefined
}

const isInformationIconProps = (
  target: any
): target is InformationIconProps => {
  return target.icon !== undefined
}

const topInformation = 'ご入力ありがとうございました。\n受付完了となります。'
const bottomInformation =
  'スタッフを手配しております。\nご連絡をお待ちください。'

/**
 * 要請後画面の情報エリアに表示するコンテント
 */
const InformationContent: React.FC<InformationContentProps> = (props) => {
  const { items, contentHeight, screenState, setScreenState } = props

  const textColor = isKsDomain()
    ? 'var(--color-text)'
    : isSaficDomain()
    ? 'var(--color-text-for-main-background)'
    : {
        [DOMAIN.SJ]: 'var(--color-text)',
      }[getDomain()]

  const pathname = useLocation().pathname

  return (
    <InformationContentAreaWrap>
      <InformationContentArea height={contentHeight}>
        {
          // 完了画面遷移時のトースト表示
          pathname === PAGE_PATH.INFORMED && (
            <PopupWindow
              screenState={screenState}
              onChangeScreenState={setScreenState}
            >
              <InformationWrap>
                <StyledText>{topInformation}</StyledText>
                <StyledText>{bottomInformation}</StyledText>
              </InformationWrap>
            </PopupWindow>
          )
        }
        {
          // 完了画面アニメーション
          pathname === PAGE_PATH.INFORMED &&
            screenState === SCREEN_STATE.SHOW_ANIMATION && <InformedAnimation />
        }
        {
          // 完了画面以降のアイコンと文章
          (pathname !== PAGE_PATH.INFORMED ||
            screenState !== SCREEN_STATE.SHOW_ANIMATION) && (
            <div>
              {items.map((item, index) =>
                isMessageProps(item) ? (
                  <TextLine key={`text-${index}`}>
                    {item.large ? (
                      <LargeText color={textColor}>{item.message}</LargeText>
                    ) : item.small ? (
                      <SmallText color={textColor}>{item.message}</SmallText>
                    ) : (
                      <MediumText color={textColor}>{item.message}</MediumText>
                    )}
                  </TextLine>
                ) : (
                  isInformationIconProps(item) && (
                    <InformationIconLine
                      key={`information-icon-${index}`}
                      marginBottom={item.marginBottom}
                    >
                      {item.icon}
                    </InformationIconLine>
                  )
                )
              )}
            </div>
          )
        }
      </InformationContentArea>
    </InformationContentAreaWrap>
  )
}

export type InformationContentProps = {
  items: Array<MessageProps | InformationIconProps>
  contentHeight?: string
  screenState?: number
  setScreenState?: (value: React.SetStateAction<number>) => void
}

type MessageProps = {
  message: string
  large?: boolean
  small?: boolean
}

type InformationIconProps = {
  icon: React.ReactNode
  marginBottom?: string
}

const backgroundColor = isKsDomain()
  ? 'rgba(240, 240, 240, 1.0)'
  : isSaficDomain()
  ? ''
  : {
      [DOMAIN.SJ]: 'rgba(240, 240, 240, 1.0)',
    }[getDomain()]

const InformationContentAreaWrap = styled.div`
  height: 400px;
  background: ${backgroundColor};
`

const InformationContentArea = styled.div<{ height?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props): string =>
    props.height ? `height: ${props.height};` : 'height: 340px'};
  position: relative;
`

const TextLine = styled.div`
  margin-bottom: 13px;
  &&:last-child {
    margin-bottom: 0px;
  }
`

const InformationIconLine = styled.div<{ marginBottom?: string }>`
  ${(props): string =>
    props.marginBottom ? `margin-bottom: ${props.marginBottom};` : ''}
  display: flex;
  justify-content: center;
`

const InformationText = styled(Text)`
  font-weight: bold;
  text-align: center;
`

const LargeText = styled(InformationText)`
  font-size: 24px;
`

const MediumText = styled(InformationText)`
  font-size: 18px;
`

const SmallText = styled(InformationText)`
  font-size: 15px;
`
const InformationWrap = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(0%, -50%);
`
const StyledText = styled(Text)`
  color: var(--color-text-for-main-background);
  line-height: 1.5;
  font-weight: bold;
  font-size: 16px;
  &:nth-child(2) {
    margin-top: 10px;
  }
`

export default InformationContent
